import { useState, useEffect } from "react";
import {
  Description,
  Dialog,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { IoMdCloseCircle } from "react-icons/io";
import { LuFileText } from "react-icons/lu";
import { PiVan } from "react-icons/pi";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
export const OrderTrack = ({ order }) => {
  let [isOpen, setIsOpen] = useState(false);

  // If no order is passed, try to get from sessionStorage
  const orderData = order || (() => {
    try {
      const checkout = sessionStorage.getItem("checkoutResponse");
      return checkout ? JSON.parse(checkout)?.orders?.[0] : null;
    } catch {
      return null;
    }
  })();

  if (!orderData) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="px-5 py-2 bg-gray-400 text-white rounded font-medium cursor-not-allowed"
        disabled
      >
        Track order
      </button>
    );
  }

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="px-5 py-2 bg-green-700 text-white rounded font-medium hover:bg-green-800"
      >
        Track order
      </button>
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
          <DialogPanel className="w-[600px] space-y-4 bg-white rounded-lg shadow-md object-cover w-full h-[250px] p-6 max-h-[90vh] overflow-y-auto">
            <div>
              <OrderTracking setIsOpen={setIsOpen} order={orderData} />
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </>
  );
};

function OrderTracking({ setIsOpen, order }) {
  const [images, setImages] = useState({}); // map productId -> imageUrl

  // Fetch product images
  useEffect(() => {
    if (!order?.order_items?.length) return;

    const idsToFetch = new Set();

    (order.order_items || []).forEach((it) => {
      if (!it.image && !images[it.product_id]) {
        idsToFetch.add(it.product_id);
      }
    });

    if (idsToFetch.size === 0) return;

    const token =
      sessionStorage.getItem("accessToken") ||
      localStorage.getItem("accessToken") ||
      "";

    const fetchForId = async (productId) => {
      try {
        const url = `${API_BASE_URL}/product?id=${encodeURIComponent(productId)}`;
        const res = await fetch(url, {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { token } : {}),
          },
        });
        if (!res.ok) return null;
        const json = await res.json();
        const result = json?.result;
        const img = result?.product_media?.[0]?.url || result?.image || null;
        return img;
      } catch (e) {
        return null;
      }
    };

    let mounted = true;
    (async () => {
      const updates = {};
      for (const id of idsToFetch) {
        if (images[id]) continue;
        const img = await fetchForId(id);
        if (img && mounted) updates[id] = img;
      }
      if (mounted && Object.keys(updates).length) {
        setImages((prev) => ({ ...prev, ...updates }));
      }
    })();

    return () => {
      mounted = false;
    };
  }, [order, images]);

  if (!order) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600">No order data available</p>
      </div>
    );
  }

  // Get order date - try multiple possible fields
  const orderDate = 
    order?.created_at
      ? new Date(order.created_at).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : order?.order_date
      ? new Date(order.order_date).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : new Date().toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });

  // Get estimated delivery date (30 days from order date)
  const baseDate = order?.created_at || order?.order_date || new Date();
  const deliveryDate = new Date(
    new Date(baseDate).getTime() + 30 * 24 * 60 * 60 * 1000
  ).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  // Get pricing info
  const pricing = order?.pricing || {};
  const subtotal = pricing.subtotal ?? 0;
  const deliveryCharges = pricing.delivery_charges ?? 0;
  const finalAmount = pricing.final_amount ?? subtotal + deliveryCharges;

  // Determine order status steps
  const statusSteps = [
    { name: "Order Confirmed", status: "confirmed" },
    { name: "Shipped", status: "shipped" },
    { name: "Out For Delivery", status: "out_for_delivery" },
    { name: "Delivered", status: "delivered" },
  ];

  // Helper function to determine if step is active
  const getStepStatus = (stepStatus) => {
    const statusMap = {
      new: "confirmed",
      pending: "confirmed",
      confirmed: "confirmed",
      shipped: "shipped",
      out_for_delivery: "out_for_delivery",
      delivered: "delivered",
      completed: "delivered",
    };

    const currentStatus = statusMap[order?.order_status] || "confirmed";
    const stepIndex = statusSteps.findIndex((s) => s.status === stepStatus);
    const currentIndex = statusSteps.findIndex(
      (s) => s.status === currentStatus
    );

    return stepIndex <= currentIndex;
  };

  // Get shipping address
  const shipping = order?.shipping_address || {};

  return (
    <div className="p-1">
      <div className="flex justify-between items-center">
        <p className="text-[30px] font-bold">Pipaan</p>
        <button onClick={() => setIsOpen(false)}>
          <IoMdCloseCircle className="text-[30px] text-green-800" />
        </button>
      </div>

      {/* Order Header */}
      <div className="my-4">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold text-[#98911F]">
            Order ID: {order?.order_number || `#${order?.order_id || "N/A"}`}
          </h1>
          <button className="flex items-center gap-2 border-2 px-2 py-1 text-sm my-2 text-green-800 border-green-300 rounded-lg hover:bg-green-50">
            <LuFileText />
            Invoice
          </button>
        </div>
        <div className="flex items-center gap-3 text-[14px] border-b-2 pb-3">
          <p className="text-gray-600">Order date: <span className="font-semibold">{orderDate}</span></p>
          <p className="border-l-2">
            <span className="flex items-center gap-2 text-green-600 font-medium pl-4">
              <PiVan />
              Estimated delivery: <span className="font-semibold">{deliveryDate}</span>
            </span>
          </p>
        </div>
      </div>

      {/* Order Status Progress */}
      <div className="flex items-center mb-6 border-b-2 pb-4">
        {statusSteps.map((step, index) => (
          <div
            key={step.status}
            className="flex-1 flex flex-col items-center justify-center"
          >
            <p
              className={`text-center text-sm ${
                getStepStatus(step.status)
                  ? "text-green-600 font-semibold"
                  : "text-gray-500"
              }`}
            >
              {step.name}
            </p>
            <span
              className={`w-4 h-4 rounded-2xl mt-2 ${
                getStepStatus(step.status) ? "bg-green-600" : "bg-gray-300"
              }`}
            ></span>
          </div>
        ))}
      </div>

      {/* Product List */}
      <div className="space-y-4 mb-6 pb-4 border-b-2">
        {(order?.order_items || []).length > 0 ? (
          (order?.order_items || []).map((item) => {
            const src = item.image || images[item.product_id] || "/image/shoeimg.png";
            return (
              <div key={item.id} className="flex items-center gap-3">
                <img
                  src={src}
                  alt={item.product_name || "Product"}
                  className="w-20 h-20 rounded object-contain bg-gray-100"
                />
                <div className="flex-1">
                  <p className="font-semibold">
                    {item.product_name || `Product ${item.product_id}`}
                  </p>
                  <p className="text-gray-500 text-sm">Qty: {item.quantity}</p>
                </div>
                <div className="ml-auto font-bold text-green-700">
                  ${Number(item.unit_price || item.total_price || 0).toFixed(2)}
                </div>
              </div>
            );
          })
        ) : (
          <p className="text-gray-500">No items in order</p>
        )}
      </div>

      {/* Payment/Delivery Info */}
      <div className="grid grid-cols-2 gap-6 mb-6 pb-4 border-b-2">
        <div>
          <h2 className="font-semibold mb-2">Payment</h2>
          <p className="text-gray-600">{order?.payment_method || "Wallet"}</p>
          <p className="text-xs text-gray-500 mt-1">
            Status: {order?.payment_status || "Pending"}
          </p>
        </div>
        <div>
          <h2 className="font-semibold mb-2">Delivery Address</h2>
          <p className="text-gray-600 text-sm">
            {shipping?.name || "N/A"}
          </p>
          <p className="text-gray-600 text-sm">
            {shipping?.address_line_1 || ""}
            {shipping?.address_line_2 ? `, ${shipping.address_line_2}` : ""}
          </p>
          <p className="text-gray-600 text-sm">
            {shipping?.city || ""} {shipping?.state || ""} {shipping?.postal_code || ""}
          </p>
          <p className="text-gray-600 text-sm">{shipping?.country || "India"}</p>
          <p className="text-gray-600 text-sm">
            Phone: {shipping?.phone || "N/A"}
          </p>
          <p className="text-gray-600 text-sm">
            Email: {shipping?.email || "N/A"}
          </p>
        </div>
      </div>

      {/* Order Summary */}
      <div>
        <h2 className="font-semibold mb-3">Order Summary</h2>
        <div className="space-y-2 text-gray-700">
          <div className="flex justify-between">
            <span>Subtotal</span>
            <span>${Number(subtotal).toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span>Delivery Charges</span>
            <span>${Number(deliveryCharges).toFixed(2)}</span>
          </div>
          <div className="flex justify-between font-bold text-lg mt-3 pt-3 border-t-2">
            <span>Total</span>
            <span className="text-green-700">${Number(finalAmount).toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}